import React, { FC } from "react";
import DashboardTile from "@components/Home/DashboardTile/DashboardTile";
import { useTranslation } from "react-i18next";
import { Box, Skeleton } from "@mui/material";
import styled from "@emotion/styled";
import { useGetAlbumsQuery } from "@store/albumApi";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import AlbumItem from "@components/AlbumItem";
import { useNavigate } from "react-router-dom";
import { Mode } from "@store/homeSlice";
import EmptyState from "@components/EmptyState/EmptyState";
import { ScreenSize } from "@constants/screen-size";

const Item = styled(Box)`
  box-sizing: border-box;
  flex: 1 1 290px;
  max-width: 290px;

  @media (max-width: ${ScreenSize.sm}px) {
    flex: 1;
    max-width: 100%;
  }
`;

const GhostLoader: FC = () => {
  return (
    <Box display="flex" width="100%" gap="24px">
      {Array.from(Array(3)).map((_, index) => (
        <Item key={index}>
          <Skeleton variant="rectangular" width="100%" height="150px" />
        </Item>
      ))}
    </Box>
  );
};

const AlbumsTile: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mode = useSelector((state: RootState) => state.home.mode);

  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );

  const { data: albums, isLoading } = useGetAlbumsQuery(
    { patientId: patientId! },
    { skip: !patientId },
  );

  const onAlbumClick = (albumId: string) => {
    if (mode === Mode.Patient) {
      return;
    }
    navigate(`../gallery/albums/${albumId}`);
  };

  return (
    <DashboardTile
      title={t(
        `${
          mode === Mode.Caregiver ? "dashboard" : "patientDashboard"
        }.albums.title`,
      )}
    >
      {!albums?.data || isLoading ? (
        <GhostLoader />
      ) : (
        <Box display="flex" gap="24px" flexWrap="wrap">
          {albums?.data?.length ? (
            albums?.data.map((album) => (
              <Item key={album.id} onClick={() => onAlbumClick(album.id)}>
                <AlbumItem album={album} mode={mode} />
              </Item>
            ))
          ) : (
            <EmptyState
              image={`${process.env.PUBLIC_URL}/images/art-albums.jpeg`}
              label={t(mode === Mode.Patient ? "patientDashboard.albums.noData" : "dashboard.albums.noData")}
            />
          )}
        </Box>
      )}
    </DashboardTile>
  );
};

export default AlbumsTile;
